import {
    IonCard,
    IonCardContent,
    IonCheckbox,
    IonCol,
    IonIcon,
    IonImg,
    IonLabel,
    IonRow,
    IonText,
    useIonToast,
} from '@ionic/react';
import { Trans, t } from '@lingui/macro';
import html2canvas from 'html2canvas';
import { informationCircleOutline } from 'ionicons/icons';
import { useAtom } from 'jotai';
import React, { memo, useCallback, useMemo, useState } from 'react';

import CustomerAppTemplate from '../../components/clientdashboardtemplate/CustomerAppTemplate';
import LoadableContent from '../../components/content/LoadableContent';
import LoadingButton from '../../components/loadingbutton/LoadingButton';
import InterventionPage from '../../components/page/InterventionPage';
import PhotoUpload from '../../components/photoUpload/PhotoUpload';
import {
    accessTokenAtom,
    guideCustomerAtom,
    jobIdAtom,
    photosListLoadableAtom,
    roomsWithDevicesAndInterventionStateAtom,
    updateDBStateAtom,
} from '../../store/Atoms';
import { trackActionsRequest, uploadImageRequest } from '../../store/Requests';
import { DeepomaticTaskGroups } from '../../types/Generic';
import ImageInfo, { ImageCategories } from '../../types/ImageInfo';
import {
    CUSTOMER_PORTAL_URL,
    MAX_IMAGE_NR,
    ROOT_URL,
} from '../../utils/constants';
import { useCustomRouter } from '../../utils/hooks/useCustomRouter';
import usePhotosUploaded from '../../utils/hooks/usePhotosUploaded';
import classes from './TabAfterClose.module.css';

const TabAfterClose: React.FC = memo(() => {
    const router = useCustomRouter();
    const [presentToast] = useIonToast();
    const [accessToken] = useAtom(accessTokenAtom);
    const [jobId] = useAtom(jobIdAtom);
    const [value] = useAtom(roomsWithDevicesAndInterventionStateAtom);
    const [photosListValue] = useAtom(photosListLoadableAtom);
    const [loading, setLoading] = useState(false);
    const photosUploaded = usePhotosUploaded();
    const [guideCustomer, setGuideCustomer] = useAtom(guideCustomerAtom);
    const [, updateDBState] = useAtom(updateDBStateAtom);
    const { downloadApp, loginToApp, saveHeaterSetup, setupHeatingSchedule } =
        guideCustomer;
    const rooms = value.state === 'hasData' ? value.data.rooms : null;
    const photosList =
        photosListValue.state === 'hasData' ? photosListValue.data : null;
    const homeName =
        value.state === 'hasData' ? value.data.jobDetails.customerId[1] : null;
    const deepomaticWorkOrderId =
        value.state === 'hasData'
            ? value.data.jobDetails.imageClassificationWorkOrderId
            : false;
    const finishInstallation =
        downloadApp &&
        loginToApp &&
        saveHeaterSetup &&
        setupHeatingSchedule &&
        photosUploaded;

    const hasCustomerAppScreenshot = useMemo(
        () =>
            photosList
                ? photosList.filter((photo) =>
                      photo.includes(
                          `_${ImageCategories.screenshotAppDashboard}_`
                      )
                  ).length > 0
                : false,
        [photosList]
    );

    const onCompleteInstallationHandler = useCallback(async () => {
        setLoading(true);
        try {
            await updateDBState();
            trackActionsRequest('complete_intervention');
            router.push('/?interventionCompleted=true', 'back');
        } catch (e) {
            presentToast({
                message: t`Oops, something went wrong while completing the installation`,
                duration: 5000,
                color: 'danger',
                buttons: [
                    {
                        text: t`Dismiss`,
                        role: 'cancel',
                    },
                ],
            });
        }
        setLoading(false);
    }, [presentToast, router, updateDBState]);
    const generateCustomerAppScreenshot = async () => {
        if (!hasCustomerAppScreenshot) {
            const element = document.getElementById(
                'customer-portal-container'
            );
            if (element) {
                try {
                    const imageMime = 'image/jpeg';
                    const imageType = 'jpeg';
                    // https://html2canvas.hertzen.com/features box-shadow not supported
                    const canvas = await html2canvas(element!);
                    const data = canvas.toDataURL(imageMime);

                    const imageInfo: ImageInfo = {
                        typepic: ImageCategories.screenshotAppDashboard,
                        typefic: imageType,
                        dataUri: data as string,
                        deepomaticTaskGroup: DeepomaticTaskGroups.CUSTOMER_APP,
                    };
                    if (deepomaticWorkOrderId) {
                        imageInfo.deepomaticWorkOrderId =
                            deepomaticWorkOrderId;
                    }
                    await uploadImageRequest(accessToken, jobId, imageInfo);
                } catch (e) {
                    console.log(
                        'Error while uploading customer app screenshot: ',
                        e
                    );
                }
            }
        }
    };

    return (
        <InterventionPage title={t`Guiding the customer`} withPadding>
            <LoadableContent value={value}>
                {!hasCustomerAppScreenshot && (
                    <div style={{ position: 'absolute', opacity: 0 }}>
                        <CustomerAppTemplate
                            rooms={rooms}
                            generateCustomerAppScreenshot={
                                generateCustomerAppScreenshot
                            }
                            homeName={homeName}
                        ></CustomerAppTemplate>
                    </div>
                )}
                <IonCard>
                    <IonCardContent>
                        <IonText>
                            <IonIcon
                                icon={informationCircleOutline}
                                size="large"
                                className="ion-float-left ion-padding-end"
                            ></IonIcon>
                            <Trans>
                                Help the customer to access the system and
                                assist them with any questions
                            </Trans>
                            .
                        </IonText>
                    </IonCardContent>
                </IonCard>
                <IonRow className="ion-margin-top">
                    <IonLabel>
                        <Trans>
                            You can find the user manual by clicking on this{' '}
                            <a
                                href={`${ROOT_URL}/static/installation/doc/Manual_A4_RTP_FR_r2.0.90e9c8648114.pdf`}
                                target="_blank"
                                rel="noreferrer"
                            >
                                link
                            </a>
                            . The customer can find it in the application in
                            the Support section.
                        </Trans>
                    </IonLabel>
                </IonRow>
                <IonRow className="ion-margin-top">
                    <Trans>Please help the customer to:</Trans>
                </IonRow>
                <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                    <IonCol>
                        <IonLabel className="ion-margin-end">
                            <Trans>
                                Guide the customer to install the application
                                or go to{' '}
                                <a
                                    href={CUSTOMER_PORTAL_URL}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    {CUSTOMER_PORTAL_URL}
                                </a>
                            </Trans>
                        </IonLabel>
                    </IonCol>
                    <IonCol size="auto">
                        <IonCheckbox
                            checked={downloadApp}
                            onIonChange={(e) =>
                                setGuideCustomer((oldValue) => ({
                                    ...oldValue,
                                    downloadApp: e.detail.checked,
                                }))
                            }
                        />
                    </IonCol>
                </IonRow>
                <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                    <IonCol>
                        <IonLabel className="ion-margin-end">
                            <Trans>
                                Guide the customer to choose their password:
                                they must click on "Request a new password" and
                                enter their email address to receive a link and
                                set it.
                            </Trans>
                        </IonLabel>
                    </IonCol>
                    <IonCol size="auto">
                        <IonCheckbox
                            checked={loginToApp}
                            onIonChange={(e) =>
                                setGuideCustomer((oldValue) => ({
                                    ...oldValue,
                                    loginToApp: e.detail.checked,
                                }))
                            }
                        />
                    </IonCol>
                </IonRow>
                <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                    <IonCol>
                        <IonLabel className="ion-margin-end">
                            <Trans>
                                Guide the customer during his first connection
                                and to answer the questionnaire to define his
                                first heating programs.
                            </Trans>
                        </IonLabel>
                    </IonCol>
                    <IonCol size="auto">
                        <IonCheckbox
                            checked={setupHeatingSchedule}
                            onIonChange={(e) =>
                                setGuideCustomer((oldValue) => ({
                                    ...oldValue,
                                    setupHeatingSchedule: e.detail.checked,
                                }))
                            }
                        />
                    </IonCol>
                </IonRow>
                <IonRow className="ion-justify-content-between ion-align-items-center ion-margin-top">
                    <IonCol>
                        <IonLabel className="no-overflow ion-margin-end">
                            <Trans>
                                Explain to the customer to not change the
                                settings on the heater (FP or Relay) to ensure
                                that the control works properly.
                            </Trans>
                        </IonLabel>
                    </IonCol>
                    <IonCol size="auto">
                        <IonCheckbox
                            checked={saveHeaterSetup}
                            onIonChange={(e) =>
                                setGuideCustomer((oldValue) => ({
                                    ...oldValue,
                                    saveHeaterSetup: e.detail.checked,
                                }))
                            }
                        />
                    </IonCol>
                </IonRow>
                <IonRow className="ion-justify-content-center ion-align-items-center ion-margin-bottom ion-margin-top">
                    <IonImg
                        className={classes.demoDashBoardContainerImage}
                        src="/assets/demo_dashboard.jpeg"
                        alt="demo_dashboard"
                    />
                </IonRow>
                <PhotoUpload
                    className="ion-margin-bottom ion-margin-top"
                    imageCode={ImageCategories.customerAppPhoto}
                    maxNumber={MAX_IMAGE_NR}
                    label={t`Take a photo of Customer App dashboard (like the one above) installed on client's phone`}
                    mandatory={true}
                    deepomaticTaskGroup={DeepomaticTaskGroups.CUSTOMER_APP}
                    deepomaticWorkOrderId={deepomaticWorkOrderId}
                ></PhotoUpload>
                <IonRow className="ion-margin-top">
                    <IonLabel>
                        <Trans>
                            You will find below the questions frequently asked
                            by customers:
                        </Trans>
                        <ul>
                            <li>
                                <Trans>
                                    What happens if I lose connection to the
                                    internet ?
                                </Trans>
                                <ul>
                                    <li>
                                        <Trans>
                                            Even if the Internet connection is
                                            lost, the system will continue to
                                            operate with the programs already
                                            defined. The D-Box must always be
                                            connected.
                                        </Trans>
                                    </li>
                                </ul>
                            </li>
                            <li className="ion-spacer-top">
                                <Trans>
                                    Why does my heater show a high temperature?
                                </Trans>
                                <ul>
                                    <li>
                                        <Trans>
                                            The radiator is set to the maximum
                                            for optimized control. Our system
                                            ensures that the temperature
                                            selected in the application is
                                            respected.
                                        </Trans>
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </IonLabel>
                </IonRow>
                <LoadingButton
                    loading={loading}
                    disabled={!finishInstallation}
                    onClick={onCompleteInstallationHandler}
                >
                    <Trans>Complete intervention</Trans>
                </LoadingButton>
            </LoadableContent>
        </InterventionPage>
    );
});

export default TabAfterClose;
